import { useMemo } from "react";
import { Stack } from "@/core/components/ui/stack";
import { toast } from "@/core/components/ui/toast";
import { BusinessSettingsSidebarHeader } from "@/business-settings/common/business-settings-sidebar-header";
import { BusinessSettingsSidebarWrapper } from "@/business-settings/common/business-settings-sidebar-wrapper";
import { getErrorMessage } from "@/core/api/errors";
import { getUserFullName } from "@/core/utils/name";
import { FilterableSpiffList } from "@/spiffs/common/filterable-spiff-list";
import { useSpiffSidebar } from "@/spiffs/context/spiff-sidebar-context";
import { useDismissSpiff, useReinstateSpiff } from "@/spiffs/query";
import { PayrollPeriod } from "@/spiffs/spiffs.d";
import { useGetBusinessUser } from "@/user/query";
import { EnrichedUserWithSpiffs } from "@/user/user";

interface PayrollPeriodUserSidebarProps {
  userId: string;
  period: PayrollPeriod;
  enrichedUsers: EnrichedUserWithSpiffs[];
  isLocked: boolean;
  businessId?: string | null;
}

export const PayrollPeriodUserSidebar = ({
  userId,
  period,
  enrichedUsers,
  isLocked,
  businessId,
}: PayrollPeriodUserSidebarProps) => {
  const { resetSidebar } = useSpiffSidebar();
  const { data: user } = useGetBusinessUser(businessId, userId);
  const { mutate: dismissSpiff } = useDismissSpiff(businessId);
  const { mutate: reinstateSpiff } = useReinstateSpiff(businessId);

  const name = user ? getUserFullName(user) : "Unknown User";

  // Find the user's spiffs for this period
  const userSpiffs = useMemo(() => {
    const enrichedUser = enrichedUsers.find((u) => u.id === userId);
    return enrichedUser?.spiffs || [];
  }, [enrichedUsers, userId]);

  const handleDismissSpiff = (spiffId: string) => {
    if (isLocked) {
      toast.error("Cannot dismiss spiffs in a locked period");
      return;
    }

    dismissSpiff(
      { spiffIds: [spiffId] },
      {
        onSuccess: () => {
          toast.success("Spiff payout dismissed");
        },
        onError: (error) => {
          toast.error("Failed to dismiss spiff payout", {
            description: getErrorMessage(error),
          });
        },
      },
    );
  };

  const handleUndismissSpiff = (spiffId: string) => {
    if (isLocked) {
      toast.error("Cannot reinstate spiffs in a locked period");
      return;
    }

    reinstateSpiff(
      { spiffId },
      {
        onSuccess: () => {
          toast.success("Spiff payout reinstated");
        },
        onError: (error) => {
          toast.error("Failed to reinstate spiff payout", {
            description: getErrorMessage(error),
          });
        },
      },
    );
  };

  return (
    <BusinessSettingsSidebarWrapper>
      <BusinessSettingsSidebarHeader
        headerText={`Spiffs for ${period.displayRange}`}
        name={name}
        onClose={resetSidebar}
      />
      <Stack
        gap="none"
        fullWidth
        className="h-full min-h-0 flex-1 pl-6 pr-2 pt-4"
      >
        <FilterableSpiffList
          spiffs={userSpiffs}
          onDismissSpiff={handleDismissSpiff}
          onUndismissSpiff={handleUndismissSpiff}
          emptyMessage={`No spiffs in this period`}
        />
      </Stack>
    </BusinessSettingsSidebarWrapper>
  );
};
